import React from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import jwt_decode from "jwt-decode";
import { io } from "socket.io-client";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import store from "../store/store";
import { logoutUser } from "../actions/authAction";
import PrivateRoute from "./PrivateRoute";
import AdminPrivateRoute from "./AdminPrivateRoute";

import Header from "../view/component/common/Header/header";
import Footer from "../view/component/common/Footer/footer";
import VerifyEmailBanner from "../view/component/common/VerifyEmailBanner";

import { VerificationProvider } from "../context/verificationContext";

import AskQuestion from "../view/AskQuestion/askQuestion";
import AddService from "../view/AddService/addService";
import Credits from "../view/Credits/credits";
import Dashboard from "../view/Dashboard/dashboard";
import EditLocation from "view/EditLocation/editLocation";
import EditProfile from "../view/EditProfile/editProfile";
import EditService from "../view/EditService/editService";
import Home from "../view/Home/home";
import LoginPage from "../view/Auth/login";
import MyProfile from "../view/Profile/myProfile";
import PageNotFound from "../view/404/pagenotfound";
import RegisterPage from "../view/Auth/register";
import ResetPage from "../view/Auth/reset";
import VerifyPage from "../view/Auth/verifyEmail";
import ServicePage from "../view/Service/service";
import SingleService from "../view/SingleService/singleService";
import Task from "../view/Task/task";
import UserProfile from "../view/Profile/userProfile";
import ForgotPassword from "../view/Auth/forgotPassword";
import ChatHistory from "../view/ChatHistory/chatHistory";
import AboutUs from "../view/AboutUs/aboutUs";
import HowItWorks from "../view/HowItWorks/howItWorks";
import Helps from "../view/Helps/helps";
import FavouriteList from "../view/FavouriteList/favouriteList";
import ContactUs from "../view/ContactUs/contactUs";
import FAQ from "view/FAQ/faq";
import CookieConsent from "view/component/cookieConsent";
import TermsCondition from "view/TermsCondition/termsCondition";
import PrivacyPolicy from "view/PrivacyPolicy/privacyPolicy";
import UnsubscribeReminders from "view/Unsubscribe/unsubscribe";
import AdminPanel from "view/Admin/adminPanel";
import AdminLogin from "view/Admin/Login/adminLogin";

const styles = {};

styles.fill = {
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
};

styles.content = {
  top: "40px",
};

persistStore(store);
if (localStorage.jwtToken) {
  const token = localStorage.jwtToken;
  const decoded = jwt_decode(token);
  const currentTime = Date.now() / 1000;
  if (decoded.exp < currentTime) {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("user");
    store.dispatch(logoutUser());
    window.location.href = "/";
  }
} else {
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("user");
  store.dispatch(logoutUser());
}

const socket = io(process.env.REACT_APP_API_HOST, {
  path: "/socket.io",
  transports: ["websocket"],     // 🔥 FORCE websocket
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  timeout: 20000,
  withCredentials: true
});

const router = () => {
  return (
    <Router>
      <Provider store={store}>
        <VerificationProvider>
        <Route
          render={({ location }) => {
            const isAdmin = location.pathname.startsWith("/admin");
            return (
              <>
                {!isAdmin && <VerifyEmailBanner />}
                {!isAdmin && <Header socket={socket} />}
                <CookieConsent />
                <TransitionGroup exit={false} className="flex flex-1 flex-col justify-center">
                  <CSSTransition
                    classNames="fade"
                    key={location.key}
                    timeout={1200}
                  >
                    <Switch>
                      <Route exact path="/" component={Home} />
                      <Route exact path="/login" component={LoginPage} />
                      <Route
                        exact
                        path="/forgot-password"
                        component={ForgotPassword}
                      />
                      {/* <Route exact path="/mockup-users" component={MockupUsers} /> */}
                      <Route exact path="/register" component={RegisterPage} />
                      <Route exact path="/r" component={RegisterPage} />
                      <Route exact path="/reset/:token" component={ResetPage} />
                      <Route exact path="/verify/:token" component={VerifyPage} />
                      <Route
                        exact
                        path="/user-profile/:id"
                        component={(props) => (
                          <UserProfile {...props} socket={socket} />
                        )}
                      />
                      <Route
                        exact
                        path="/single-service"
                        component={(props) => <SingleService {...props} socket={socket} />}
                      />
                      <Route exact path="/about-us" component={AboutUs} />
                      <Route exact path="/how-it-works" component={HowItWorks} />
                      <Route exact path="/help" component={Helps} />
                      <PrivateRoute
                        exact
                        path="/service"
                        component={(props) => <ServicePage {...props} socket={socket} />}
                      />

                      <PrivateRoute
                        exact
                        path="/dashboard"
                        component={(props) => (
                          <Dashboard {...props} socket={socket} />
                        )}
                      />
                      <Route exact path="/admin/login" component={AdminLogin} />
                      <AdminPrivateRoute path="/admin" component={AdminPanel} />
                      <PrivateRoute exact path="/my-profile" component={MyProfile} />
                      <PrivateRoute path="/ask-question" component={(props) => <AskQuestion {...props} socket={socket} />} />
                      <PrivateRoute
                        path="/task"
                        // Keyed on the task so opening another task's
                        // notification while on this page loads that task
                        // instead of staying on the old one.
                        component={(props) => (
                          <Task
                            {...props}
                            key={new URLSearchParams(props.location.search).get("taskId")}
                            socket={socket}
                          />
                        )}
                      />
                      <PrivateRoute exact path="/edit-profile" component={EditProfile} />
                      <PrivateRoute exact path="/credits" component={Credits} />
                      <PrivateRoute exact path="/add-service" component={AddService} />
                      <PrivateRoute exact path="/edit-service" component={EditService} />
                      <PrivateRoute exact path="/edit-location" component={EditLocation} />
                      <PrivateRoute exact path="/chat-history" component={(props) => <ChatHistory {...props} socket={socket} />} />
                      <PrivateRoute exact path="/favourite-list" component={FavouriteList} />
                      <Route exact path="/faq" component={FAQ} />
                      <Route exact path="/contact-us" component={ContactUs} />
                      <Route exact path="/terms-condition" component={TermsCondition} />
                      <Route exact path="/privacy-policy" component={PrivacyPolicy} />
                      {/* Target of the unsubscribe link in reminder emails.
                          Public: the recipient may not be logged in.
                          /public/... is the path earlier emails were sent
                          with — kept so those links keep working. */}
                      <Route
                        exact
                        path={[
                          "/unsubscribe-reminders",
                          "/public/unsubscribe-reminders",
                        ]}
                        component={UnsubscribeReminders}
                      />
                      <Route exact path="*" component={PageNotFound} />
                    </Switch>
                  </CSSTransition>
                </TransitionGroup>
                {!isAdmin && <Footer />}
              </>
            );
          }}
        />
        </VerificationProvider>
      </Provider>
    </Router>
  );
};
export default router;
